/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useIsAuthenticated from 'modules/common/auth/hooks/use-is-authenticated';
import { EXECUTION_TYPE } from 'modules/common/ui-controller';
import ROUTES from 'modules/common/constants/route';
import { selectIsLandingPageUpdated, selectLandingPage } from 'modules/users/selectors';
import {
  isTabAllowedForRole,
  resolveTabFromHash,
  setHotelCommand,
  splitCommandUrl,
} from 'modules/common/ui-controller/utils/functions';
import { runCommandSet } from 'modules/common/ui-controller/hooks/use-apply-command-url';
import { dashboardActions } from '../slice';
import {
  selectHotelChangeState,
  selectIsSavedCommandFilterLoaded,
  selectShareUrlTabList,
  selectTabChangeState,
  selectUserRole,
  selectVisualFilters,
} from '../selectors';
import { isEmpty } from '../../common/helpers/object';

/**
 * Custom hook to execute custom landing page state
 */
const useCustomLanding = (setTabValue) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useIsAuthenticated();
  const customLandingRef = useRef(null);
  // landing page url that is already applied, to apply it only once
  const appliedLandingRef = useRef(null);
  // whether landing page commands are waiting for tab and hotel changes
  const pendingCommandsRef = useRef(false);
  const tabChange = useSelector(selectTabChangeState);
  const tabs = useSelector(selectShareUrlTabList);
  const hotelChange = useSelector(selectHotelChangeState);
  const userRole = useSelector(selectUserRole);
  const customLandingPage = useSelector(selectLandingPage);
  const isLandingPageUpdated = useSelector(selectIsLandingPageUpdated);
  const visualFilters = useSelector(selectVisualFilters);
  const IsSavedCommandFilterLoaded = useSelector(selectIsSavedCommandFilterLoaded);
  //
  const [isValidUrl, setIsValidUrl] = useState(true);
  //
  customLandingRef.current = () => {
    // retrieve search string from url
    const { search } = splitCommandUrl(customLandingPage?.landingPage);
    // execute url filter options
    runCommandSet(dispatch, search, EXECUTION_TYPE.CONVERSATION);
  };
  //
  useEffect(() => {
    // call for command execution function after tab and hotel commands are executed
    if (
      tabChange &&
      hotelChange &&
      customLandingPage &&
      Boolean(customLandingPage?.landingPage) &&
      Boolean(!location?.search) &&
      !isLandingPageUpdated &&
      !IsSavedCommandFilterLoaded &&
      pendingCommandsRef.current
    ) {
      pendingCommandsRef.current = false;
      customLandingRef.current();
    }
  }, [tabChange, hotelChange]);

  useEffect(() => {
    if (
      isAuthenticated() &&
      customLandingPage &&
      Boolean(customLandingPage?.landingPage) &&
      Boolean(!location?.search) &&
      isValidUrl &&
      !isLandingPageUpdated &&
      !IsSavedCommandFilterLoaded &&
      visualFilters &&
      appliedLandingRef.current !== customLandingPage.landingPage
    ) {
      const { search, hash } = splitCommandUrl(customLandingPage.landingPage);
      // tab part is completed when there is no tab to set or it has been set
      let isTabApplied = !hash;
      if (!isEmpty(tabs) && hash) {
        dispatch(dashboardActions.setTabChange(false));
        const tab = resolveTabFromHash(hash);
        if (tab === undefined) {
          isTabApplied = true;
        } else if (isAuthenticated()) {
          if (!isTabAllowedForRole(tab, userRole)) {
            navigate(ROUTES.DASHBOARD);
            setIsValidUrl(false);
            return;
          }
          setTabValue(tabs[tab]);
          dispatch(dashboardActions.setUrlTab(tabs[tab]));
          dispatch(dashboardActions.setTabChange(true));
          setIsValidUrl(true);
          isTabApplied = true;
        }
      }
      // retrieve hotel command and set hotel name
      if (search) {
        dispatch(dashboardActions.setHotelChange(false));
        setHotelCommand(search, EXECUTION_TYPE.CONVERSATION);
      }
      pendingCommandsRef.current = true;
      if (isTabApplied) {
        appliedLandingRef.current = customLandingPage.landingPage;
      }
    }
  }, [
    customLandingPage,
    isLandingPageUpdated,
    visualFilters,
    location,
    tabs,
    dispatch,
    navigate,
    userRole,
    isAuthenticated,
    setTabValue,
  ]);
};

export default useCustomLanding;
